import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BookOpen, TrendingUp, Upload, Users, ArrowRight, Clock, AlertTriangle, ClipboardPaste } from "lucide-react";
import { useClasseContext } from "../context/ClasseContext";
import { useUser } from "../context/UserContext";
import { useClasse } from "../hooks/fetch_api_hook/useClasse";
import { useGroupe } from "../hooks/fetch_api_hook/useGroupe";
import { getAccessType } from "../utils/permissions";

/**
 * Carte d'option du hub Créer — gabarit maquette 06/2026 :
 * teinte d'ancrage par carte, badge "Le plus utilisé" sur la recommandée,
 * métadonnées passives (formats en texte, pas en pills), CTA différencié
 * (verbe d'action), flèche animée au survol. La carte entière est un <button>
 * (clavier/lecteur d'écran gratuits) — le CTA est un span stylé en bouton.
 * Graisses maquette (Nunito globale depuis le 12/06) : titres 800,
 * descriptions 600, métas 700, CTA 800 — c'est la densité qui fait le style.
 */
function CarteCreation({ icone: Icone, iconeBg, iconeCouleur, titre, description, meta, temps, cta, ctaPrimaire = false, reco = false, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`group relative flex flex-col items-stretch text-left bg-white border-2 rounded-2xl p-5 w-full h-full cursor-pointer transition-all duration-150 hover:-translate-y-1 focus-visible:-translate-y-1 outline-none ${
                reco
                    ? 'border-blue-500 shadow-lg shadow-blue-500/15 hover:shadow-xl hover:shadow-blue-500/25'
                    : 'border-[#D5E6F3] shadow-sm hover:border-cyan-400 hover:shadow-xl hover:shadow-slate-900/10'
            }`}
        >
            {reco && (
                <span className="absolute -top-3 left-4 bg-orange-500 text-white text-[11px] font-extrabold tracking-wide px-3 py-1 rounded-full border-2 border-white shadow-md shadow-orange-500/30">
                    ⭐ Le plus utilisé
                </span>
            )}

            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${iconeBg}`}>
                <Icone className={`w-5 h-5 ${iconeCouleur}`} />
            </div>

            <h3 className="text-[17px] font-extrabold text-slate-800 leading-tight tracking-tight">{titre}</h3>
            <p className="text-[13.5px] font-semibold text-slate-500 leading-relaxed mt-1.5 mb-2.5 flex-1">{description}</p>

            {/* Métadonnées passives : formats séparés par des points médians */}
            <div className="flex items-center gap-1.5 flex-wrap text-xs font-bold text-slate-400 mb-3.5">
                {meta.map((m, i) => (
                    <span key={m} className="flex items-center gap-1.5">
                        {i > 0 && <span className="opacity-50">·</span>}
                        {m}
                    </span>
                ))}
                {temps && (
                    <span className="ml-auto inline-flex items-center gap-1 bg-[#E5EFFF] text-blue-600 text-[11px] font-extrabold px-2 py-0.5 rounded-full">
                        <Clock className="w-3 h-3" /> {temps}
                    </span>
                )}
            </div>

            <span
                className={`w-full py-2.5 rounded-xl text-sm font-extrabold flex items-center justify-center gap-1.5 transition-colors ${
                    ctaPrimaire
                        ? 'bg-blue-600 text-white group-hover:bg-blue-700'
                        : 'bg-[#EDF6FC] text-blue-600 group-hover:bg-[#DEEEFA]'
                }`}
            >
                {cta}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </span>
        </button>
    );
}

const CreerEvaluation = () => {
    const { idclasse } = useParams();
    const navigate = useNavigate();
    const { setClasseDetail, classeNom, groupNom, classesGroup } = useClasseContext();
    const { user, etablissementData } = useUser();

    const { data: classeData } = useClasse(idclasse, classeNom === "" && "not-enabled", false);
    const { data: groupeData } = useGroupe(idclasse + "/", groupNom === "" && "not-enabled", false);
    const donneesClasse = classeNom ? classeData : groupeData;

    // Pousse la donnée fetchée dans ClasseContext : la sidebar ne fetch rien elle-même,
    // elle lit classeDetail depuis le contexte. Ce rôle était tenu par DataLoader quand
    // le clic classe atterrissait sur le tableau de bord.
    useEffect(() => {
        if (donneesClasse) {
            setClasseDetail(donneesClasse);
        }
    }, [donneesClasse, setClasseDetail]);

    const accessType = useMemo(() => {
        if (!idclasse || !user || !etablissementData) return 'none';
        return getAccessType(parseInt(idclasse, 10), user, etablissementData);
    }, [idclasse, user, etablissementData]);

    const currentClasse = etablissementData?.classes?.find(c => c.id === parseInt(idclasse));
    const hasEleves = currentClasse ? currentClasse.nombre_eleves > 0 : true;

    const isCordo = !!etablissementData?.professeurs?.find(p => p.id === user?.id)?.coordonateur;

    const isNiveauEvalNat =
        (classeNom && (classeNom[0] === "6" || classeNom[0] === "5" || classeNom[0] === "4")) ||
        (classesGroup.length !== 0 && classesGroup[0] && (classesGroup[0][0] === "6" || classesGroup[0][0] === "5"));

    return (
        <div className="flex-1 px-6 py-8 lg:px-10 lg:py-10 text-black bg-gradient-to-br from-[#EAF4FB] via-[#DCEFF9] to-[#D2EBF8] min-h-full">
            <div className="mb-8">
                <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight">Créer une évaluation</h1>
                <p className="text-sm font-bold text-slate-500 mt-1">
                    {classeNom ? `Classe ${classeNom}` : `Groupe ${groupNom}`} · choisissez votre point de départ
                </p>
            </div>

            {/* ── Classe sans élèves : composer reste possible (préparer un
                sujet papier est légitime), mais on annonce la limite AVANT
                d'entrer dans le flux — le LaunchModal ne proposera que le
                format papier, et la saisie de résultats sera bloquée. ── */}
            {!hasEleves && (
                <div className="flex items-start gap-3 mb-5 px-4 py-3 rounded-xl border border-amber-300 bg-amber-50">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                        <strong>Classe sans élèves</strong> : vous pouvez composer un sujet, mais seule
                        l'évaluation papier sera disponible. Importez les élèves pour lancer des
                        passations et saisir des résultats.
                    </p>
                </div>
            )}

            {/* ── Les 3 options du prof ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
                <CarteCreation
                    reco
                    ctaPrimaire
                    icone={BookOpen}
                    iconeBg="bg-[#E5EFFF]"
                    iconeCouleur="text-[#2D7FF9]"
                    titre="Composer une évaluation en 5 minutes"
                    description="Choisissez vos attendus, TanoEd compose le sujet : QCM, relier, classer, ordonner, tableaux, géométrie interactive…"
                    meta={["En ligne", "Cartes en classe", "PDF imprimable"]}
                    temps="~5 min"
                    cta="Composer"
                    onClick={() => navigate(`/home/qcm/${idclasse}`)}
                />

                {accessType === 'full' && isNiveauEvalNat && (
                    <CarteCreation
                        icone={TrendingUp}
                        iconeBg="bg-[#EFEAFB]"
                        iconeCouleur="text-[#7C5CD6]"
                        titre="Mesurer les progrès depuis les nationales"
                        description="Ré-évaluez vos élèves avec les questions nationales et comparez avec leurs résultats des évaluations nationales."
                        meta={["En ligne", "PDF imprimable"]}
                        cta="Créer le suivi"
                        onClick={() => navigate(`/home/eval-bilan/${idclasse}`)}
                    />
                )}

                {accessType === 'full' && hasEleves && (
                    <CarteCreation
                        icone={Upload}
                        iconeBg="bg-[#E2F4FC]"
                        iconeCouleur="text-[#0E9CD4]"
                        titre="J'ai déjà mon sujet"
                        description="Déposez votre sujet, reliez-le aux attendus évalués, puis saisissez les notes."
                        meta={["Suivi de notation"]}
                        cta="Importer mon sujet"
                        onClick={() => navigate(`/home/evals/${idclasse}/add`)}
                    />
                )}

                {accessType === 'full' && hasEleves && (
                    <CarteCreation
                        icone={ClipboardPaste}
                        iconeBg="bg-[#E9F9EF]"
                        iconeCouleur="text-[#1FA25B]"
                        titre="Coller mes notes existantes"
                        description="Copiez votre liste de notes depuis votre logiciel habituel ou un tableur, collez-la : l'évaluation et les résultats sont créés d'un coup."
                        meta={["Presse-papier", "Notes converties en maîtrise"]}
                        temps="~1 min"
                        cta="Coller mes notes"
                        onClick={() => navigate(`/home/coller-notes/${idclasse}`)}
                    />
                )}
            </div>

            {/* ── Section coordonnateur ── */}
            {isCordo && (
                <>
                    <div className="flex items-center gap-3 mt-8 mb-3.5">
                        <span className="flex items-center gap-2 text-xs font-extrabold tracking-widest uppercase text-slate-500">
                            En tant que coordonnateur
                            <span className="bg-amber-400 text-amber-900 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full normal-case tracking-normal">
                                Coordo
                            </span>
                        </span>
                        <span className="flex-1 h-px bg-slate-900/10 rounded" />
                    </div>

                    {/* Carte horizontale, volontairement plus discrète que les 3 du prof */}
                    <button
                        onClick={() => navigate(`/home/add-common-eval`)}
                        className="group flex flex-col sm:flex-row sm:items-center gap-4 text-left bg-white border-2 border-[#D5E6F3] rounded-2xl px-5 py-4 max-w-lg w-full cursor-pointer shadow-sm transition-all duration-150 hover:-translate-y-1 hover:border-cyan-400 hover:shadow-xl hover:shadow-slate-900/10 outline-none focus-visible:-translate-y-1"
                    >
                        <div className="w-11 h-11 rounded-xl bg-[#E2F7F1] flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-[#1FA98C]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-[15.5px] font-extrabold text-slate-800 tracking-tight">Devoir commun de niveau</h3>
                            <p className="text-[12.5px] font-semibold text-slate-500 leading-relaxed mt-0.5">
                                Pour toutes les classes ou groupes d'un niveau, avec affectation des professeurs.
                            </p>
                        </div>
                        <span className="bg-[#EDF6FC] text-blue-600 group-hover:bg-[#DEEEFA] text-sm font-extrabold px-4 py-2.5 rounded-xl flex items-center justify-center gap-1.5 flex-shrink-0 transition-colors">
                            Créer le devoir
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </span>
                    </button>
                </>
            )}

            <div className="mt-7">
                <button
                    onClick={() => navigate(`/home/detail/${idclasse}`)}
                    className="inline-flex items-center gap-1.5 text-sm font-extrabold text-blue-600 hover:text-blue-700 hover:underline transition-colors duration-200"
                >
                    Voir le tableau de bord <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default CreerEvaluation;
